import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import * as Actions from "actions/user/user-base.actions";

const Input = styled("input")({
  display: "none",
});

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  "& .MuiAvatar-root": {
    width: 200,
    height: 200,
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[4],
  },
}));

const ImageOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  borderRadius: "50%",
  opacity: 0,
  transition: "opacity 0.3s",
  "&:hover": {
    opacity: 1,
  },
});

const VirtualTryOnProfileImage = (props) => {
  const {
    serviceError,
    reqStatus,
    isLoading,
    tryonProfileImage,
    uploadTryOnProfileImage,
    getTryOnProfileImage,
    deleteTryOnProfileImage,
  } = props;

  React.useEffect(() => {
    // Load profile image on mount
    getTryOnProfileImage();
  }, []);

  const sendNotification = (type, { title, description = "" }) => {
    Notification[type]({
      message: title,
      description,
    });
  };

  React.useEffect(() => {
    if (serviceError) {
      const feedback = {
        title: "Something went wrong!",
        description: serviceError.statusText,
      };
      sendNotification("error", feedback);
    }
    switch (reqStatus) {
      default:
        break;
    }
  }, [serviceError, reqStatus]);


  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert("Image size must be less than 10MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    // Create FormData and upload
    const formData = new FormData();
    formData.append("user_image", file);
    
    uploadTryOnProfileImage(formData);
  };

  const handleDelete = () => {
    if (!window.confirm("Are you sure you want to delete your profile image?")) {
      return;
    }

    deleteTryOnProfileImage();
  };

  const getValidationMessage = () => {
    if (tryonProfileImage) {
      return "Image should contain a clear, front-facing photo of a person";
    }
    return "";
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Virtual Try-On Profile Image
              <Tooltip title="Upload your photo once for instant try-ons on all products">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a clear, front-facing photo for the best virtual try-on results
            </Typography>
          </Box>

            {/* Alerts */}
            {serviceError && reqStatus && reqStatus.includes("FAILURE") && (
              <Alert severity="error">
                {serviceError.statusText || "An error occurred"}
              </Alert>
            )}

            {reqStatus === "UPLOAD_TRYON_PROFILE_IMAGE_SUCCESS" && (
              <Alert severity="success">
                Profile image uploaded successfully!
              </Alert>
            )}

            {reqStatus === "DELETE_TRYON_PROFILE_IMAGE_SUCCESS" && (
              <Alert severity="success">
                Profile image deleted successfully
              </Alert>
            )}

            {/* Image Display */}
            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
              {tryonProfileImage ? (
                <ProfileImageContainer>
                  <Avatar
                      variant="square"
                    src={tryonProfileImage.cdn?.secure_url}
                    alt="Your virtual try-on profile"
                  />
                  <ImageOverlay>
                    <Stack direction="row" spacing={1}>
                      <label htmlFor="update-profile-image">
                        <Input
                          accept="image/*"
                          id="update-profile-image"
                          type="file"
                          onChange={handleFileSelect}
                          disabled={isLoading}
                        />
                        <Tooltip title="Update image">
                          <IconButton
                            component="span"
                            sx={{ color: "white" }}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <CircularProgress size={24} sx={{ color: "white" }} />
                            ) : (
                              <UploadIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </label>
                      <Tooltip title="Delete image">
                        <IconButton
                          onClick={handleDelete}
                          sx={{ color: "white" }}
                          disabled={isLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </ImageOverlay>
                </ProfileImageContainer>
              ) : (
                <Box textAlign="center">
                  <Avatar
                      variant="square"
                    sx={{
                      width: 300,
                      height: 400,
                      margin: "0 auto",
                      bgcolor: "grey.200",
                    }}
                  >
                    <UploadIcon sx={{ fontSize: 80, color: "grey.400" }} />
                  </Avatar>
                  <Typography variant="body2" color="text.secondary" mt={2}>
                    No profile image set
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Validation Message */}
            {getValidationMessage() && (
              <Alert severity="info" icon={<CheckIcon />}>
                {getValidationMessage()}
              </Alert>
            )}

            {/* Upload Button */}
            {!tryonProfileImage && (
              <Box display="flex" justifyContent="center">
                <label htmlFor="upload-profile-image">
                  <Input
                    accept="image/*"
                    id="upload-profile-image"
                    type="file"
                    onChange={handleFileSelect}
                    disabled={isLoading}
                  />
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={isLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                    disabled={isLoading}
                    size="large"
                  >
                    {isLoading ? "Uploading..." : "Upload Profile Image"}
                  </Button>
                </label>
              </Box>
            )}

          {/* Guidelines */}
          <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Image Guidelines:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Front-facing photo with your face clearly visible</li>
              <li>Good lighting and plain background preferred</li>
              <li>Show upper body (shoulders and above)</li>
              <li>Avoid sunglasses or face coverings</li>
              <li>Maximum file size: 10MB</li>
              <li>Supported formats: JPG, PNG, WebP</li>
            </Typography>
          </Box>

          {/* Benefits */}
          <Box sx={{ bgcolor: "primary.50", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom color="primary.main">
              Why upload a profile image?
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Try on any product instantly without uploading your photo each time</li>
              <li>Faster and more convenient shopping experience</li>
              <li>Consistent quality across all virtual try-ons</li>
              <li>Your image is secure and private</li>
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Mapping Props and State
const virtualTryOnProfileImage = connect(
  ({ userbootupInfo }) => ({
    reqStatus: userbootupInfo.reqStatus,
    isLoading: userbootupInfo.isLoading,
    serviceError: userbootupInfo.serviceError,
    tryonProfileImage: userbootupInfo.tryonProfileImage,
  }),
  (dispatch) =>
    bindActionCreators(
      {...Actions}, dispatch)
)(VirtualTryOnProfileImage);

export { virtualTryOnProfileImage as default };
